import React, {
  createContext,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import Config from '@assetron/common/config';
import { GoogleAuthProvider, signInWithCredential } from 'firebase/auth';
import { auth } from '@assetron/common/contexts/AuthWebContext';
import { postIdTokensAndRedirect } from '../utils/auth-helper/postIdTokensAndRedirect';

export interface GoogleOneTapContextType {
  isProcessing: boolean;
  showOneTap: () => void;
}

const GoogleOneTapContext = createContext<GoogleOneTapContextType | undefined>(
  undefined,
);

export function GoogleOneTapProvider({
  children,
}: Readonly<{
  children: React.ReactNode;
}>) {
  const { APP_URL, ASSETRON_GOOGLE_CLIENT_ID } = Config;
  const [isProcessing, setIsProcessing] = useState(false);
  const initialized = useRef(false);
  const isAutomationEnvironment =
    typeof navigator !== 'undefined' && navigator.webdriver;

  const handleCredentialResponse = useCallback(
    async (response: { credential?: string | null }) => {
      if (isAutomationEnvironment) {
        return;
      }

      if (response?.credential) {
        setIsProcessing(true);
        try {
          const cred = GoogleAuthProvider.credential(response.credential);
          const result = await signInWithCredential(auth, cred);
          await postIdTokensAndRedirect(result, APP_URL, APP_URL);
        } catch {
          setIsProcessing(false);
        }
      }
    },
    [APP_URL, isAutomationEnvironment],
  );

  const initializeGoogle = useCallback(() => {
    if (
      initialized.current ||
      isAutomationEnvironment ||
      !globalThis.google?.accounts?.id
    )
      return;
    initialized.current = true;

    globalThis.google.accounts.id.initialize({
      client_id: ASSETRON_GOOGLE_CLIENT_ID,
      callback: handleCredentialResponse,
      use_fedcm_for_prompt: true,
      use_fedcm_for_button: true,
    });
  }, [
    ASSETRON_GOOGLE_CLIENT_ID,
    handleCredentialResponse,
    isAutomationEnvironment,
  ]);

  const showOneTap = useCallback(() => {
    if (isAutomationEnvironment) {
      return;
    }

    initializeGoogle();
    globalThis.google?.accounts?.id?.prompt();
  }, [initializeGoogle, isAutomationEnvironment]);

  const contextValue = useMemo(
    () => ({ isProcessing, showOneTap }),
    [isProcessing, showOneTap],
  );

  return (
    <GoogleOneTapContext.Provider value={contextValue}>
      {children}
    </GoogleOneTapContext.Provider>
  );
}
export default GoogleOneTapContext;
